import type { EcFormView } from "../api";
import { formatIndexNear, formatNumberNear, formatPercentNear, type Locale, type Marks } from "@ncp/i18n";
import type { EcInvestigationReport, ReportText } from "@ncp/api-client";

export const EMPTY_TEXT: ReportText = { ar: "", en: "" };

/** Where the reviewer stands on a passage. Nothing is "done" until they have said so. */
export type SectionDecision = "pending" | "accepted" | "overridden";

/** A figure the rules engine computed, shown beside the passage it supports. */
export interface Evidence {
  label: string;
  value: string;
  /** Renders a tick or a cross when the figure is a test against a threshold. */
  met?: boolean;
  strong?: boolean;
}

/** One reviewable passage of the report: where it lives in the object, what it claims, and what backs it. */
export interface ReportSection {
  path: string;
  chapter: ChapterKey;
  label: string;
  /** What the reviewer is being asked to check, in one line. */
  hint: string;
  value: ReportText;
  /** Load-bearing passages — a conclusion the recommendation rests on. */
  conclusion?: boolean;
  evidence: Evidence[];
}

export const CHAPTERS = ["header", "parties", "jurisdiction", "test1", "test2", "nature", "assessment", "harm", "observations"] as const;
export type ChapterKey = (typeof CHAPTERS)[number];

export function readText(report: EcInvestigationReport, path: string): ReportText {
  const value = path.split(".").reduce<unknown>((acc, key) => (acc as Record<string, unknown> | undefined)?.[key], report);
  return (value as ReportText | undefined) ?? EMPTY_TEXT;
}

/** Immutably writes a passage back at `path`, so React sees a new object at every level that changed. */
export function writeText(report: EcInvestigationReport, path: string, value: ReportText): EcInvestigationReport {
  const keys = path.split(".");
  const clone = <T,>(node: T, depth: number): T => {
    if (depth === keys.length - 1) return { ...node, [keys[depth]]: value } as T;
    const key = keys[depth] as keyof T;
    return { ...node, [key]: clone(node[key], depth + 1) } as T;
  };
  return clone(report, 0);
}

export const isEmptyText = (t: ReportText) => !t.ar?.trim() && !t.en?.trim();

type Translate = (key: string, vars?: Record<string, unknown>) => string;

/**
 * The passages of the report in the order the Department writes them, each with the deterministic figures that bear on it.
 * The reviewer never has to leave the passage to find the number it rests on.
 */
/** The marks of the published calculation method the evidence is read against (GET /api/analysis/method); none is held here. */
export interface SectionMarks {
  deltaHhiSignificant?: number; marketShareThresholdPercent?: number;
  /** Condition 2.9: the HHI bands, the ΔHHI marks and the control test's stakes, each figure printed in full where rounding would reach one. */
  hhiMarks?: number[]; deltaHhiMarks?: number[]; controlMarks?: number[];
}

/** The fields the report builder adds to each market under the published method (overlap, most affected, unallocated share). */
type MethodMarket = EcInvestigationReport["assessment"]["markets"][number] & { overlap?: boolean; mostAffected?: boolean; unallocatedShare?: number };

/** The most affected market by the method's one rule – flagged by the server; a market without overlap is never it. */
export function mostAffectedMarket(r: EcInvestigationReport): MethodMarket | undefined {
  return (r.assessment.markets as MethodMarket[]).find((m) => m.mostAffected);
}

export function buildSections(r: EcInvestigationReport, form: EcFormView | undefined, t: Translate, locale: string, given: SectionMarks = {}): ReportSection[] {
  const ar = locale === "ar";
  // The report's own threshold rows carry the share threshold of the policy it was built with.
  const marks: SectionMarks = { marketShareThresholdPercent: r.legalTest2.thresholds[0]?.shareThresholdPercent, ...given };
  // The platform's one rule (condition 2.9): a share at one decimal and an index or an amount as a whole number, each in full where rounding
  // would put it on or across a mark it is read against – the share threshold, the HHI bands, the ΔHHI marks, the control stakes.
  const lc: Locale = ar ? "ar" : "en";
  const n = (value: number, against: Marks = undefined) => formatIndexNear(value, lc, against);
  const pct = (value: number, against: Marks = marks.marketShareThresholdPercent) => formatPercentNear(value, lc, against);
  const strongDelta = (delta: number) => marks.deltaHhiSignificant != null && delta >= marks.deltaHhiSignificant;
  const S = (key: string) => t(`backoffice:investigationReport.sections.${key}`);
  const H = (key: string, vars?: Record<string, unknown>) => t(`backoffice:investigationReport.hints.${key}`, vars);
  const E = (key: string) => t(`backoffice:investigationReport.evidence.${key}`);
  const marketName = (m: { productMarket: string; productMarketAr: string }) => (ar ? m.productMarketAr : m.productMarket);
  const nameOf = (x: { name: string; nameAr?: string | null }) => (ar && x.nameAr ? x.nameAr : x.name);
  const geography = (g: string) => (ar ? GEOGRAPHY_AR[g] ?? g : g);

  const partyEvidence: Evidence[] = r.parties.map((p) => ({ label: t(`backoffice:investigationReport.capacities.${p.capacity}`, { defaultValue: p.capacity }), value: nameOf(p) }));
  const marketEvidence: Evidence[] = r.legalTest2.thresholds.map((row) => ({ label: marketName(row), value: geography(row.geographicMarket) }));
  const thresholdEvidence: Evidence[] = r.legalTest2.thresholds.flatMap((row) => [
    { label: `${marketName(row)} — ${E("sales")}`, value: `${formatNumberNear(row.partiesTurnoverAed, lc, row.turnoverThresholdAed, 0)} / ${n(row.turnoverThresholdAed)}`, met: row.turnoverMet },
    { label: `${marketName(row)} — ${E("share")}`, value: `${pct(row.combinedSharePercent, row.shareThresholdPercent)} / ${pct(row.shareThresholdPercent)}`, met: row.shareMet },
  ]);
  const control = form?.qualification.control;
  const controlEvidence: Evidence[] = control
    ? [
        { label: E("controlVerdict"), value: t(`backoffice:workspace.verdict.${control.verdict}`, { defaultValue: control.verdict }), strong: true },
        { label: E("changeOfControl"), value: control.changeOfControl ? t("misc.yes") : t("misc.no") },
        // A stake is read against the control test's marks, not the share threshold.
        ...(form?.acquiredStakePercent != null ? [{ label: E("acquiredStake"), value: pct(form.acquiredStakePercent, marks.controlMarks) }] : []),
        ...(form?.preTransactionStakePercent != null ? [{ label: E("preStake"), value: pct(form.preTransactionStakePercent, marks.controlMarks) }] : []),
        ...(form?.controlRights.length ? [{ label: E("controlRights"), value: form.controlRights.map((k) => t(`backoffice:workspace.controlRights.${k}`, { defaultValue: k })).join(ar ? "، " : ", ") }] : []),
      ]
    : [];

  const marketMetrics = (index: number): Evidence[] => {
    const m = r.assessment.markets[index] as MethodMarket | undefined;
    if (!m) return [];
    const overlap = m.overlap !== false;
    return [
      overlap ? { label: E("combinedShare"), value: pct(m.combinedShareAfter), met: m.dominanceAfter, strong: true } : { label: t("backoffice:r3Metrics.partiesShare"), value: `${pct(m.combinedShareAfter)} · ${t("backoffice:r3Metrics.noOverlap")}` },
      { label: E("dominanceBefore"), value: m.dominanceBefore ? t("misc.yes") : t("misc.no") },
      { label: E("hhi"), value: overlap ? `${n(m.hhiPre, marks.hhiMarks)} ${ar ? "←" : "→"} ${n(m.hhiPost, marks.hhiMarks)}` : n(m.hhiPre, marks.hhiMarks) },
      overlap ? { label: E("deltaHhi"), value: n(m.deltaHhi, marks.deltaHhiMarks), strong: strongDelta(m.deltaHhi) } : { label: E("deltaHhi"), value: t("backoffice:r3Metrics.noOverlap") },
      ...(m.unallocatedShare ? [{ label: t("backoffice:r3Metrics.unallocated"), value: pct(m.unallocatedShare, undefined) }] : []),
      { label: E("band"), value: t(`enums.band.${m.band}`, { defaultValue: m.band }) },
      ...m.shares.slice(0, 5).map((s) => ({ label: nameOf(s), value: pct(s.percent), strong: s.isParty })),
    ];
  };
  const worst = mostAffectedMarket(r);
  const worstEvidence: Evidence[] = worst
    ? [
        { label: E("worstMarket"), value: marketName(worst), strong: true },
        { label: E("combinedShare"), value: pct(worst.combinedShareAfter), met: worst.dominanceAfter },
        { label: E("deltaHhi"), value: n(worst.deltaHhi, marks.deltaHhiMarks), strong: strongDelta(worst.deltaHhi) },
        { label: t("backoffice:r3Metrics.competitorsIdentified"), value: `${worst.shares.filter((s) => !s.isParty && !/^(others?|imports|independent|rest)\b/i.test(s.name)).length}` },
      ]
    : [{ label: E("worstMarket"), value: t("backoffice:r3Metrics.noOverlapAnywhere") }];

  // Condition 2.14: a hint that names a threshold names the one the report's table was computed against.
  const firstRow = r.legalTest2.thresholds[0];
  const hintVars = {
    turnover: firstRow ? (ar ? `${n(firstRow.turnoverThresholdAed)} درهم` : `AED ${n(firstRow.turnoverThresholdAed)}`) : "—",
    share: firstRow ? pct(firstRow.shareThresholdPercent) : marks.marketShareThresholdPercent != null ? pct(marks.marketShareThresholdPercent) : "—",
  };
  const section = (path: string, chapter: ChapterKey, labelKey: string, hintKey: string, evidence: Evidence[], conclusion = false): ReportSection => ({
    path, chapter, label: S(labelKey), hint: H(hintKey, hintVars), value: readText(r, path), conclusion, evidence,
  });

  const sections: ReportSection[] = [
    section("header.parties", "header", "headerParties", "headerParties", partyEvidence),
    section("header.sector", "header", "headerSector", "headerSector", marketEvidence),
    ...r.parties.map((p, i) => ({
      path: `parties.${i}.description`,
      chapter: "parties" as ChapterKey,
      label: `${nameOf(p)} — ${S("partyDescription")}`,
      hint: H("partyDescription"),
      value: readText(r, `parties.${i}.description`),
      evidence: [{ label: E("capacity"), value: t(`backoffice:investigationReport.capacities.${p.capacity}`, { defaultValue: p.capacity }), strong: true }],
    })),
    section("jurisdiction.competence", "jurisdiction", "competence", "competence", marketEvidence),
    section("jurisdiction.requestStatus", "jurisdiction", "requestStatus", "requestStatus", [
      { label: E("acceptedOn"), value: r.jurisdiction.acceptedOn || "—" },
      { label: E("legalBasis"), value: ar ? r.jurisdiction.legalBasis.ar : r.jurisdiction.legalBasis.en },
    ]),
    section("legalTest1.introduction", "test1", "test1Intro", "test1Intro", controlEvidence),
    section("legalTest1.ownershipTransfer", "test1", "ownershipTransfer", "ownershipTransfer", controlEvidence),
    section("legalTest1.transactionValue", "test1", "transactionValue", "transactionValue",
      form?.considerationAed != null ? [{ label: E("consideration"), value: ar ? `${n(form.considerationAed)} درهم` : `AED ${n(form.considerationAed)}`, strong: true }] : []),
    section("legalTest1.ownershipConclusion", "test1", "ownershipConclusion", "ownershipConclusion", controlEvidence, true),
    section("legalTest1.control", "test1", "control", "control", controlEvidence),
    section("legalTest1.structureBefore", "test1", "structureBefore", "structure", partyEvidence),
    section("legalTest1.structureAfter", "test1", "structureAfter", "structure", partyEvidence),
    section("legalTest1.conclusion", "test1", "test1Conclusion", "test1Conclusion", controlEvidence, true),
    section("legalTest2.productMarket", "test2", "productMarket", "productMarket", marketEvidence),
    section("legalTest2.geographicMarket", "test2", "geographicMarket", "geographicMarket", marketEvidence),
    section("legalTest2.substitution", "test2", "substitution", "substitution", marketEvidence),
    section("legalTest2.marketDefinitionResult", "test2", "marketDefinitionResult", "marketDefinitionResult", marketEvidence, true),
    section("legalTest2.thresholdConclusion", "test2", "thresholdConclusion", "thresholdConclusion", thresholdEvidence, true),
  ];

  if (r.variant === "ThresholdsUnmet") {
    sections.push(section("legalTest2.nonNotificationJustification", "test2", "nonNotification", "nonNotification", thresholdEvidence, true));
  } else {
    sections.push(
      section("natureReasoning", "nature", "nature", "nature", [
        { label: E("nature"), value: t(`backoffice:investigationReport.natures.${r.header.nature}`, { defaultValue: r.header.nature }), strong: true },
      ]),
      section("assessment.competitiveSituation", "assessment", "competitiveSituation", "competitiveSituation", worstEvidence),
      ...r.assessment.markets.map((m, i) => ({
        path: `assessment.markets.${i}.commentary`,
        chapter: "assessment" as ChapterKey,
        label: `${marketName(m)} — ${E("hhi")}`,
        hint: H("marketCommentary"),
        value: readText(r, `assessment.markets.${i}.commentary`),
        evidence: marketMetrics(i),
      })),
      section("assessment.theoryOfHarm.introduction", "harm", "harmIntro", "harmIntro", worstEvidence),
      section("assessment.theoryOfHarm.horizontalNonCoordinated", "harm", "harmNonCoordinated", "harmNonCoordinated", worstEvidence),
      section("assessment.theoryOfHarm.horizontalCoordinated", "harm", "harmCoordinated", "harmCoordinated", worstEvidence),
      section("assessment.theoryOfHarm.vertical", "harm", "harmVertical", "harmVertical", worstEvidence),
      section("assessment.theoryOfHarm.conglomerate", "harm", "harmConglomerate", "harmConglomerate", worstEvidence),
      section("assessment.theoryOfHarm.riskConclusion", "harm", "riskConclusion", "riskConclusion", worstEvidence, true),
      section("assessment.theoryOfHarm.rebuttal", "harm", "rebuttal", "rebuttal", worstEvidence),
    );
  }

  sections.push(
    ...r.finalObservations.map((_, i) => ({
      path: `finalObservations.${i}`,
      chapter: "observations" as ChapterKey,
      label: `${S("finalObservation")} ${i + 1}`,
      hint: H("finalObservation"),
      value: readText(r, `finalObservations.${i}`),
      conclusion: true,
      evidence: [{ label: E("recommendation"), value: t(`backoffice:investigationReport.recommendations.${r.recommendationCode}`), strong: true }],
    })),
  );
  return sections;
}

/** The chapters actually present in this report, with the range of steps each covers. */
export function chapterSpans(sections: ReportSection[]): Array<{ chapter: ChapterKey; from: number; count: number }> {
  const spans: Array<{ chapter: ChapterKey; from: number; count: number }> = [];
  sections.forEach((s, i) => {
    const last = spans[spans.length - 1];
    if (last && last.chapter === s.chapter) last.count += 1;
    else spans.push({ chapter: s.chapter, from: i, count: 1 });
  });
  return spans;
}

const GEOGRAPHY_AR: Record<string, string> = {
  "United Arab Emirates": "دولة الإمارات العربية المتحدة",
  UAE: "دولة الإمارات العربية المتحدة",
  "Abu Dhabi": "أبوظبي",
  Dubai: "دبي",
  Sharjah: "الشارقة",
  Ajman: "عجمان",
  "Umm Al Quwain": "أم القيوين",
  "Ras Al Khaimah": "رأس الخيمة",
  Fujairah: "الفجيرة",
  GCC: "دول مجلس التعاون الخليجي",
};
